import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { Container, Table, Badge, Card, Row, Col, Form, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search, Clock, Calendar2Check, Globe, FileEarmarkText, Funnel, Person } from "react-bootstrap-icons";

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [sortBy, setSortBy] = useState("created_at");

  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      try {
        const [reqs, langs, forms] = await Promise.all([
          supabase
            .from("requests")
            .select(`
              *,
              projects!project_id (name),
              profiles!user_id (full_name),
              content_types!content_type_id (name),
              source_lang:languages!source_language (name, flag)
            `)
            .order("created_at", { ascending: false }),
          supabase.from("languages").select("id, name, flag"),
          supabase.from("formats").select("id, name")
        ]);

        if (reqs.error) throw reqs.error;
        
        setRequests(reqs.data || []);
        setLanguages(langs.data || []);
        setFormats(forms.data || []);
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, []);

  // Helper: Map Array IDs to Human Names
  const getNamesFromIds = (ids, lookupTable) => {
    if (!ids || !Array.isArray(ids)) return "";
    return ids
      .map(id => lookupTable.find(item => String(item.id) === String(id))?.name)
      .filter(Boolean)
      .join(", ");
  };

  // Helper for Time Remaining
  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;
    const diff = new Date(deadline) - new Date();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return { days, isOverdue: diff < 0 };
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const searchStr = searchTerm.toLowerCase();
      const title = String(req.title || "").toLowerCase();
      const reqNum = String(req.request_number || "").toLowerCase();
      const requester = String(req.profiles?.full_name || "").toLowerCase();

      const matchesSearch = title.includes(searchStr) || reqNum.includes(searchStr) || requester.includes(searchStr);
      const matchesStatus = statusFilter === "all" || req.status === statusFilter;
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      if (sortBy === "deadline") return new Date(a.deadline) - new Date(b.deadline);
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [requests, searchTerm, statusFilter, sortBy]);

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <Container fluid style={{ width: '96%', maxWidth: '1900px' }} className="py-4">
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h2 className="fw-bold text-dark"><Funnel className="me-2 text-primary"/>Manager Operations Queue</h2>
        </div>
        <div className="text-end">
            <Badge bg="dark" className="p-2 px-3 fs-6 shadow-sm">{filteredRequests.length} Total Requests</Badge>
        </div>
      </div>

      <Card className="shadow-sm border-0 mb-3 bg-white">
        <Card.Body className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0"><Search size={14}/></InputGroup.Text>
                <Form.Control 
                  className="bg-light border-start-0"
                  placeholder="Search title, ID, or requester..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select className="bg-light" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="submitted">Submitted (Pending)</option>
                <option value="validated">Validated</option>
                <option value="completed">Completed</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select className="bg-light" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="created_at">Sort by: Date Created</option>
                <option value="deadline">Sort by: Deadline</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 overflow-hidden">
        <Table hover responsive className="mb-0 custom-manager-table" style={{ tableLayout: 'fixed' }}>
          <thead className="table-dark text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            <tr>
              <th style={{ width: '130px' }}>Created</th>
              <th style={{ width: '160px' }}>Requester / Project</th>
              <th style={{ width: '220px' }}>Request Title</th>
              <th style={{ width: '160px' }}>Deadline</th>
              <th style={{ width: '160px' }}>Content / Formats</th>
              <th style={{ width: '110px' }}>Source</th>
              <th>Target Locales</th>
              <th style={{ width: '120px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => {
              const remaining = getTimeRemaining(req.deadline);
              return (
                <tr 
                  key={req.id} 
                  // CRITICAL FIX: Navigates using request_number to match Review page expectation
                  onClick={() => navigate(`/manager/review/${req.request_number}`)} 
                  style={{ cursor: 'pointer', verticalAlign: 'top' }}
                >
                  <td>
                    <div className="fw-bold">{new Date(req.created_at).toISOString().split('T')[0]}</div>
                    <div className="text-muted small">{new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</div>
                  </td>
                  <td>
                    <div className="text-truncate fw-bold d-flex align-items-center">
                        <Person size={12} className="me-1 text-primary"/> {req.profiles?.full_name || "Unknown"}
                    </div>
                    <div className="mt-1">
                        <Badge bg="info" text="dark" style={{ fontSize: '0.65rem' }}>{req.projects?.name || "General"}</Badge>
                    </div>
                  </td>
                  <td>
                    <div className="fw-bold text-dark text-truncate" title={req.title}>{req.title}</div>
                    <small className="text-muted font-monospace" style={{ fontSize: '0.7rem' }}>#{req.request_number}</small>
                  </td>
                  <td>
                    <div className={remaining?.isOverdue ? "text-danger fw-bold" : "fw-bold"}>
                      <Calendar2Check size={12} className="me-1"/>
                      {req.deadline ? req.deadline.replace('T', ' ').slice(0, 16) : 'No Deadline'}
                    </div>
                    {remaining && (
                      <div className={`small fw-bold mt-1 ${remaining.isOverdue ? 'text-danger' : 'text-primary'}`}>
                         {remaining.isOverdue ? '!! OVERDUE !!' : `${remaining.days}d remaining`}
                      </div>
                    )}
                  </td>
                  <td className="small">
                    <div className="fw-bold text-dark">
                        <FileEarmarkText size={12} className="me-1 text-secondary"/>
                        {req.content_types?.name || "Standard Content"}
                    </div>
                    <div className="text-muted text-truncate mt-1" style={{ fontSize: '0.75rem' }}>
                        {getNamesFromIds(req.format_ids, formats) || "N/A"}
                    </div>
                  </td>
                  <td>
                    <span className="p-1 px-2 rounded bg-primary bg-opacity-10 text-primary fw-bold border border-primary border-opacity-25">
                        {req.source_lang?.flag} {req.source_lang?.name || 'EN'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {req.target_languages?.map(langId => {
                        const lang = languages.find(l => String(l.id) === String(langId));
                        return (
                          <Badge key={langId} bg="light" text="dark" className="border font-weight-normal" style={{ fontSize: '0.65rem' }}>
                            {lang ? `${lang.flag} ${lang.name}` : '...'}
                          </Badge>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <Badge pill bg={req.status === 'submitted' ? 'warning' : 'success'} className="text-uppercase w-100 py-2 shadow-sm" style={{ fontSize: '0.65rem', color: req.status === 'submitted' ? '#000' : '#fff' }}>
                      {req.status}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <style>{`
        .custom-manager-table td { 
            padding: 14px 10px; 
            font-size: 0.82rem; 
            border-bottom: 1px solid #f0f0f0; 
            white-space: nowrap; 
        }
        .custom-manager-table tr:hover { 
            background-color: #f8fbff !important; 
            box-shadow: inset 4px 0 0 #0d6efd;
        }
        .text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .bg-info { background-color: #e1f5fe !important; color: #01579b !important; }
      `}</style>
    </Container>
  );
}